import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useStateHandler from "@/hooks/useStateHandler";
import {
  applySourceToArtworkList,
  createSuarteworldViewSource,
  resolveArtworkViewSource,
} from "@/shared/lib/artworkViewSource";
import { getSsrArtworkBootstrap } from "@/utils/ssrArtworkBootstrap";

import {
  matchesArtworkRouteContext,
  resolveArtworkSlug,
  sanitizeArtworkFeedPayload,
} from "./artworkFeedState.utils";
import { isArtworkRoutingDebugEnabled } from "./artworkRoutingDebug.utils";
import useArtworkHydration from "./useArtworkHydration";
import useArtworkLoadController from "./useArtworkLoadController";
import useArtworkRecoveryEffects from "./useArtworkRecoveryEffects";
import useArtworkRouteContext, { normaliseArtworkRouteParam } from "./useArtworkRouteContext";
import useArtworkSlideController from "./useArtworkSlideController";
import useArtworkVisitTelemetry from "./useArtworkVisitTelemetry";

const ARTWORK_CACHE_INVALIDATION_ACTIONS = ["EDIT_ARTWORK", "DELETE_ARTWORK", "BUY_ARTWORK", "FOLLOW_ACTION"];

export default function useArtwork() {
  const { cacheHandler, stateHandler, state } = useStateHandler();
  const navigate = useNavigate();
  const {
    slug,
    location,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
    requestedViewParam,
    buildArtworkEndpoint,
    resolveHoldingContext,
  } = useArtworkRouteContext();

  // Tracks the cache key for the current artworks list so we can keep updating it
  const listCacheKeyRef = useRef(location.state?.cacheKey || null);

  const artworkCacheKey = useMemo(
    () => `artwork_${slug}${holdingId ? `_holding_${holdingId}` : ""}`,
    [slug, holdingId]
  );

  const [artworks, setArtworks] = useState(() => initArtworks()); /** AWESOME BUG. DO NOT DO THIS. ALWAYS USE A FUNCTION ()=>. IF NOT, IT GETS CALLED EVERYTIME because of javascript, not react */
  const [activeSlide, setActiveSlide] = useState(() => initActiveSlide());
  const [loading, setLoading] = useState(artworks ? false : true);
  const [error, setError] = useState(null);
  const loadMoreLoading = useRef(false);
  const activeArtworkRequestRef = useRef(null);
  const routeRewriteTimestampsRef = useRef([]);
  const routeSyncCandidateRef = useRef(null);
  const previousSlugRef = useRef(slug);
  const suarteworldSegmentStatusRef = useRef("idle"); // idle | loading | exhausted
  const feedIdentityRef = useRef("");
  const nextLoadMoreOffsetRef = useRef(null);
  const exhaustedLoadMoreEndpointsRef = useRef(new Set());
  const shouldLoadMore = (Boolean(artworks) && artworks.data.length < artworks.totalDocs && Boolean(artworks.endpoint));
  const viewerSessionId = state?.user_session?._id ?? state?.user_session?.id ?? null;

  const { logRouting, postVisitOnce } = useArtworkVisitTelemetry({
    location,
    slug,
    activeSlide,
    artworks,
    listCacheKeyRef,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
  });
  const {
    enrichedSlugsRef,
    markEnrichedSlug,
    markArtworksAsEnriched,
    hydrateArtworkDetails,
  } = useArtworkHydration({
    cacheHandler,
    artworks,
    setArtworks,
    listCacheKeyRef,
    locationCacheKey: location.state?.cacheKey,
    buildArtworkEndpoint,
    resolveHoldingContext,
    viewerSessionId,
    logRouting,
    cacheInvalidationActions: ARTWORK_CACHE_INVALIDATION_ACTIONS,
  });

  useEffect(() => {
    const cacheIdentity = location.state?.cacheKey ?? listCacheKeyRef.current ?? "";
    const endpointIdentity = artworks?.endpoint ?? "";
    const nextIdentity = `${cacheIdentity}::${endpointIdentity}`;

    if (feedIdentityRef.current !== nextIdentity) {
      feedIdentityRef.current = nextIdentity;
      suarteworldSegmentStatusRef.current = "idle";
      nextLoadMoreOffsetRef.current = null;
      exhaustedLoadMoreEndpointsRef.current = new Set();
      loadMoreLoading.current = false;
      logRouting("feed-identity-change", { identity: nextIdentity });
    }
  }, [artworks?.endpoint, location.state?.cacheKey]);

  const replaceLastDirectorytoNavigate = (nextSlug, nextSearchParams = null, viewContextOverride, reason = "unspecified") => {
    const paths = location.pathname.split('/').filter(Boolean); // Split and remove any empty segments
    paths.pop(); // Remove the last directory
    paths.push(nextSlug); // Add the new directory
    let newPath = `/${paths.join('/')}`; // Reconstruct the path
    let searchSuffix = "";

    if (nextSearchParams) {
      let paramsInstance;
      if (nextSearchParams instanceof URLSearchParams) {
        paramsInstance = nextSearchParams;
      } else if (typeof nextSearchParams === "string") {
        paramsInstance = new URLSearchParams(nextSearchParams.startsWith("?") ? nextSearchParams.slice(1) : nextSearchParams);
      } else if (typeof nextSearchParams === "object") {
        paramsInstance = new URLSearchParams();
        Object.entries(nextSearchParams).forEach(([key, value]) => {
          if (value === undefined || value === null) {
            return;
          }
          paramsInstance.set(key, `${value}`);
        });
      }

      if (paramsInstance) {
        const built = paramsInstance.toString();
        if (built.length > 0) {
          searchSuffix = `?${built}`;
        }
      }
    }

    newPath = `${newPath}${searchSuffix}`;

    // Preserve navigation metadata while ensuring the cache key stays in sync
    const nextState = { ...(location.state || {}), from: true };

    if (listCacheKeyRef.current) {
      nextState.cacheKey = listCacheKeyRef.current;
    } else if (nextState.cacheKey === undefined) {
      delete nextState.cacheKey;
    }

    if (viewContextOverride !== undefined) {
      if (viewContextOverride === null) {
        delete nextState.artworkViewContext;
      } else {
        nextState.artworkViewContext = viewContextOverride;
      }
    }

    logRouting("replace-route", {
      reason,
      from: `${location.pathname}${location.search || ""}`,
      to: newPath,
      nextSlug,
    });

    if (isArtworkRoutingDebugEnabled()) {
      const now = Date.now();
      const windowMs = 2500;
      const nextTimestamps = routeRewriteTimestampsRef.current
        .filter((timestamp) => now - timestamp < windowMs);
      nextTimestamps.push(now);
      routeRewriteTimestampsRef.current = nextTimestamps;

      if (nextTimestamps.length >= 6) {
        logRouting("replace-route-burst", {
          count: nextTimestamps.length,
          windowMs,
          target: newPath,
        });
      }
    }

    navigate(newPath, { replace: true, state: nextState }); // Navigate to the new path
  };

  const pruneInaccessibleArtworkFromFeed = (matchOptions = {}, options = {}) => {
    const { redirectIfActive = false, reason = "prune-inaccessible-artwork" } = options;
    let removedIndex = -1;
    let nextPayload = null;
    let nextActiveIndex = activeSlide;

    setArtworks((prev) => {
      if (!prev || !Array.isArray(prev.data) || prev.data.length === 0) {
        return prev;
      }

      removedIndex = prev.data.findIndex((item) => matchesArtworkRouteContext(item, matchOptions));
      if (removedIndex === -1) {
        return prev;
      }

      const nextData = prev.data.filter((_, index) => index !== removedIndex);
      nextActiveIndex = removedIndex < activeSlide
        ? Math.max(activeSlide - 1, 0)
        : Math.min(activeSlide, Math.max(nextData.length - 1, 0));

      nextPayload = {
        ...prev,
        data: nextData,
        totalDocs: Math.min(
          Number.isFinite(prev.totalDocs) ? prev.totalDocs - 1 : nextData.length,
          nextData.length
        ),
      };

      return nextPayload;
    });

    if (!nextPayload) {
      return false;
    }

    const keyToStore = listCacheKeyRef.current || location.state?.cacheKey;
    if (keyToStore) {
      cacheHandler.storeInCache(keyToStore, nextPayload, {
        invalidateWhen: ARTWORK_CACHE_INVALIDATION_ACTIONS,
      });
    }

    if (removedIndex !== -1) {
      setActiveSlide(nextActiveIndex);
    }

    if (redirectIfActive && removedIndex === activeSlide) {
      const fallbackArtwork = nextPayload.data[nextActiveIndex] ?? null;
      const fallbackSlug = resolveArtworkSlug(fallbackArtwork, null);
      if (fallbackArtwork && fallbackSlug) {
        const fallbackHoldingContext = resolveHoldingContext(fallbackArtwork);
        const fallbackSearchParams = new URLSearchParams();
        if (fallbackHoldingContext.holdingId) {
          fallbackSearchParams.set("holding_id", fallbackHoldingContext.holdingId);
        }
        if (fallbackHoldingContext.ownerId) {
          fallbackSearchParams.set("owner_id", fallbackHoldingContext.ownerId);
        }
        if (fallbackHoldingContext.holdingType) {
          fallbackSearchParams.set("holding_type", fallbackHoldingContext.holdingType);
        }
        if (fallbackHoldingContext.holdingId) {
          fallbackSearchParams.set("view", "holding");
        }

        replaceLastDirectorytoNavigate(
          fallbackSlug,
          fallbackSearchParams,
          fallbackHoldingContext.holdingId ? { type: "holding", ...fallbackHoldingContext } : null,
          reason
        );
      }
    }

    logRouting("artwork-pruned-from-feed", {
      reason,
      removedIndex,
      nextLength: nextPayload.data.length,
      matchedSlug: matchOptions.slug ?? null,
      matchedHoldingId: matchOptions.holdingId ?? null,
    });

    return true;
  };

  useArtworkRecoveryEffects({
    artworks,
    activeSlide,
    slug,
    viewerSessionId,
    setArtworks,
    cacheHandler,
    listCacheKeyRef,
    locationCacheKey: location.state?.cacheKey,
    buildArtworkEndpoint,
    resolveHoldingContext,
    pruneInaccessibleArtworkFromFeed,
    cacheInvalidationActions: ARTWORK_CACHE_INVALIDATION_ACTIONS,
  });

  function initArtworks() {
    const ssrBootstrap = getSsrArtworkBootstrap(slug);
    if (ssrBootstrap?.data) {
      const clonedSsrData = sanitizeArtworkFeedPayload({
        totalDocs: 1,
        data: [ssrBootstrap.data],
      });
      const resolvedSource = resolveArtworkViewSource({
        itemSource: clonedSsrData?.data?.[0]?._artworkViewSource,
        navigationSource: location.state?.artworkViewSource,
        pathname: location.pathname,
      }) ?? createSuarteworldViewSource();

      if (Array.isArray(clonedSsrData?.data)) {
        clonedSsrData.data = applySourceToArtworkList(clonedSsrData.data, resolvedSource, true);
      }

      return clonedSsrData;
    }

    // 1) Prefer explicit cacheKey passed via navigation state
    const valueFromCacheKey = location.state?.cacheKey ? cacheHandler.getCacheValue(location.state.cacheKey) : null;

    if (Boolean(valueFromCacheKey) && !valueFromCacheKey.isDataInvalid) {
      listCacheKeyRef.current = location.state.cacheKey;
      return sanitizeArtworkFeedPayload(valueFromCacheKey.data);
    }

    // 2) Try to recover from any cached artwork list that contains this slug
    try {
      const cacheMetas = stateHandler.getValue('cache_metas') || {};
      const cacheKeys = Object.keys(cacheMetas);
      for (let i = 0; i < cacheKeys.length; i++) {
        const key = cacheKeys[i];
        if (!key.startsWith('artworks_of_')) continue;
        const candidate = cacheHandler.getCacheValue(key);
        if (!candidate || candidate.isDataInvalid) continue;
        const dataArr = candidate.data?.data || [];
        const containsSlug = dataArr.findIndex(a => (a?.slug ? a.slug === slug : false)) !== -1;
        if (containsSlug) {
          listCacheKeyRef.current = key;
          return sanitizeArtworkFeedPayload(candidate.data);
        }
      }
    } catch (_) { /* noop: fallback to single artwork */ }

    // 3) Fallback: single artwork cache (used when landing directly on an artwork)
    const valueFromArtworkId = cacheHandler.getCacheValue(artworkCacheKey);
    if (Boolean(valueFromArtworkId) && !valueFromArtworkId.isDataInvalid) {
      return sanitizeArtworkFeedPayload(valueFromArtworkId.data);
    }

    return null;
  }

  function resolveActiveSlideIndex(payload = artworks) {
    if (!payload || !Array.isArray(payload.data)) {
      return 0;
    }

    if (holdingId) {
      const indexOfHolding = payload.data.findIndex((artwork) => {
        const candidates = [
          artwork?.holding_id,
          artwork?.product_id,
          artwork?.productId,
          artwork?.metadata?.limited_edition?.copy_id,
          artwork?.product_metadata?.limited_edition?.copy_id,
        ];
        return candidates.some((candidate) => normaliseArtworkRouteParam(candidate) === holdingId);
      });

      if (indexOfHolding !== -1) {
        return indexOfHolding;
      }
    }

    const indexOfArtwork = payload.data.findIndex(
      (artwork) => resolveArtworkSlug(artwork, artwork?.product_id ?? artwork?._id) === slug
    );
    return indexOfArtwork !== -1 ? indexOfArtwork : 0;
  }

  function initActiveSlide() {
    return resolveActiveSlideIndex(artworks);
  }

  function rehydrateArtworksFromCache() {
    const cachedPayload = initArtworks();
    if (!cachedPayload) {
      return null;
    }

    const clonedPayload = sanitizeArtworkFeedPayload(cachedPayload);
    const resolvedSource = resolveArtworkViewSource({
      navigationSource: location.state?.artworkViewSource,
      pathname: location.pathname,
    });
    if (resolvedSource && Array.isArray(clonedPayload.data)) {
      clonedPayload.data = applySourceToArtworkList(clonedPayload.data, resolvedSource);
    }

    markArtworksAsEnriched(clonedPayload);
    setArtworks(clonedPayload);
    setActiveSlide(resolveActiveSlideIndex(clonedPayload));
    setLoading(false);
    setError(null);

    return clonedPayload;
  }
  useArtworkLoadController({
    slug,
    artworks,
    activeSlide,
    viewerSessionId,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
    location,
    previousSlugRef,
    enrichedSlugsRef,
    markEnrichedSlug,
    logRouting,
    postVisitOnce,
    rehydrateArtworksFromCache,
    buildArtworkEndpoint,
    resolveHoldingContext,
    setLoading,
    setError,
    setArtworks,
    setActiveSlide,
    cacheHandler,
    artworkCacheKey,
    cacheInvalidationActions: ARTWORK_CACHE_INVALIDATION_ACTIONS,
    pruneInaccessibleArtworkFromFeed,
    listCacheKeyRef,
    activeArtworkRequestRef,
  });
  useArtworkSlideController({
    activeSlide,
    artworks,
    shouldLoadMore,
    location,
    logRouting,
    loadMoreLoading,
    nextLoadMoreOffsetRef,
    exhaustedLoadMoreEndpointsRef,
    setArtworks,
    cacheHandler,
    listCacheKeyRef,
    cacheInvalidationActions: ARTWORK_CACHE_INVALIDATION_ACTIONS,
    suarteworldSegmentStatusRef,
    slug,
    previousSlugRef,
    routeSyncCandidateRef,
    resolveHoldingContext,
    holdingId,
    holdingOwnerId,
    holdingTypeParam,
    requestedViewParam,
    replaceLastDirectorytoNavigate,
  });

  useEffect(() => {
    previousSlugRef.current = slug;
    routeSyncCandidateRef.current = null;
  }, [slug]);

  useEffect(() => {
    if (!artworks) {
      return;
    }

    const visitSlug = resolveArtworkSlug(artworks.data[activeSlide], slug);
    postVisitOnce(visitSlug);
  }, [artworks, activeSlide, slug, viewerSessionId]);

  useEffect(() => {
    if (!artworks || !Array.isArray(artworks.data) || artworks.data.length === 0) {
      return;
    }

    const hydrationTargets = [];
    // The route loader owns hydration for the active slide. This hook only
    // prepares upcoming slides so it cannot race the route loader and replace
    // the visible artwork while authentication and view context settle.
    for (let offset = 1; offset < 3; offset += 1) {
      const candidate = artworks.data[activeSlide + offset];
      if (!candidate || candidate?._enriched === true) {
        continue;
      }
      hydrationTargets.push({
        artwork: candidate,
        reason: `lookahead-${offset}`,
      });
    }

    hydrationTargets.forEach(({ artwork, reason }) => {
      void hydrateArtworkDetails(artwork, reason);
    });
  }, [activeSlide, artworks, viewerSessionId]);

  return { loading, error, artworks, shouldLoadMore, activeSlide, setActiveSlide };
}
